import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { forgetPlace } from "../lib/lastPlace";
import { dropDevice } from "../lib/push";
import { clearDrafts } from "../lib/storage";
import { flushWaiting } from "../state/deferred";
import { api, type Level, type Schemas, type Tool, unwrap } from "./client";

export const keys = {
  me: ["me"] as const,
  people: (status: string) => ["people", status] as const,
  teams: ["teams"] as const,
  team: (id: string) => ["team", id] as const,
  preview: (id: string) => ["team", id, "preview"] as const,
  resources: ["resources"] as const,
  settings: ["settings"] as const,
};

export const meQuery = {
  queryKey: keys.me,
  queryFn: () => unwrap(api.GET("/api/me")),
  retry: false,
  staleTime: 60_000,
} as const;

export function useMe() {
  return useQuery(meQuery);
}

export function usePeople(status: "active" | "deactivated" | "all" = "active") {
  return useQuery({
    queryKey: keys.people(status),
    queryFn: () => unwrap(api.GET("/api/people", { params: { query: { status } } })),
  });
}

export function useTeams(includeArchived = false) {
  // R9-A5: admins may list the archived teams too ("Show archived"); a key under the list's,
  // so whatever refreshes the teams refreshes this.
  return useQuery({
    queryKey: includeArchived ? [...keys.teams, "archived"] : keys.teams,
    queryFn: () =>
      unwrap(api.GET("/api/teams", { params: { query: { include_archived: includeArchived } } })),
  });
}

export function useTeam(id: string | undefined) {
  return useQuery({
    queryKey: keys.team(id ?? ""),
    enabled: Boolean(id),
    queryFn: () =>
      unwrap(api.GET("/api/teams/{team_id}", { params: { path: { team_id: id ?? "" } } })),
  });
}

export function useTeamPreview(id: string | undefined) {
  return useQuery({
    queryKey: keys.preview(id ?? ""),
    enabled: Boolean(id),
    queryFn: () =>
      unwrap(api.GET("/api/teams/{team_id}/preview", { params: { path: { team_id: id ?? "" } } })),
  });
}

export function useResources() {
  return useQuery({ queryKey: keys.resources, queryFn: () => unwrap(api.GET("/api/resources")) });
}

export function useSyncNow() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => unwrap(api.POST("/api/resources/sync")),
    onSuccess: () => invalidate("resources", "team", "people"),
  });
}

export function useSettings() {
  return useQuery({ queryKey: keys.settings, queryFn: () => unwrap(api.GET("/api/settings")) });
}

/** The release running (D28): "v1.1.0" and its commit; "dev" on a stack built by hand. */
export function useVersion() {
  return useQuery({
    queryKey: ["health", "version"],
    queryFn: () => unwrap(api.GET("/api/health/version")),
    staleTime: 10 * 60_000,
  });
}

export function useHealth() {
  const ready = useQuery({
    queryKey: ["health", "ready"],
    queryFn: async () => {
      // 503 still carries the body ({status, db, dbos}), so read it either way.
      const r = await fetch("/api/health/ready", { credentials: "same-origin" });
      return (await r.json()) as { status: string; db: boolean; dbos: boolean };
    },
    refetchInterval: 30_000,
  });
  const components = useQuery({
    queryKey: ["health", "components"],
    queryFn: () => unwrap(api.GET("/api/health/components")),
    refetchInterval: 30_000,
  });
  return { ready, components };
}

function useInvalidate() {
  const qc = useQueryClient();
  return (...prefixes: string[]) =>
    Promise.all(prefixes.map((p) => qc.invalidateQueries({ queryKey: [p] })));
}

export function useCreatePerson() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: Schemas["PersonCreate"]) => unwrap(api.POST("/api/people", { body })),
    // Its form shows the error in place (R10-S4: no second toast).
    meta: { quiet: true },
    onSuccess: () => invalidate("people", "teams", "team"),
  });
}

export function useUpdatePerson() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Schemas["PersonUpdate"] }) =>
      unwrap(api.PATCH("/api/people/{person_id}", { params: { path: { person_id: id } }, body })),
    // Its form shows the error in place (R10-S4: no second toast).
    meta: { quiet: true },
    onSuccess: () => invalidate("people", "teams", "team", "me"),
  });
}

export function useDeactivatePerson() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST("/api/people/{person_id}/deactivate", { params: { path: { person_id: id } } }),
      ),
    onSuccess: () => invalidate("people", "teams", "team"),
  });
}

/** P2-A9: settle a link the sync held: one of the accounts it found, or not needed. */
export function useSettleHeldLink() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, tool, body }: { id: string; tool: Tool; body: Schemas["HeldLinkIn"] }) =>
      unwrap(
        api.POST("/api/people/{person_id}/accounts/{tool}", {
          params: { path: { person_id: id, tool } },
          body,
        }),
      ),
    onSuccess: () => invalidate("people"),
  });
}

/** R9-A6: bring a deactivated person back. */
export function useReactivatePerson() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST("/api/people/{person_id}/reactivate", { params: { path: { person_id: id } } }),
      ),
    onSuccess: () => invalidate("people", "teams", "team", "conversations"),
  });
}

export function useCreateTeam() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: Schemas["TeamCreate"]) => unwrap(api.POST("/api/teams", { body })),
    // Its form shows the error in place (R10-S4: no second toast).
    meta: { quiet: true },
    onSuccess: () => invalidate("teams"),
  });
}

export type TeamSave = {
  id: string;
  update?: Schemas["TeamUpdate"];
  members?: string[];
  grants?: { resource_id: string; level: Level }[];
  powers?: string[];
};

/** Saves a team editor draft: only the parts that changed are sent. */
export function useSaveTeam() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, update, members, grants, powers }: TeamSave) => {
      const path = { params: { path: { team_id: id } } };
      if (update) await unwrap(api.PATCH("/api/teams/{team_id}", { ...path, body: update }));
      if (members)
        await unwrap(
          api.PUT("/api/teams/{team_id}/members", { ...path, body: { person_ids: members } }),
        );
      if (grants?.length)
        await unwrap(api.PUT("/api/teams/{team_id}/grants", { ...path, body: { grants } }));
      if (powers)
        await unwrap(api.PUT("/api/teams/{team_id}/powers", { ...path, body: { powers } }));
    },
    // Archiving or unarchiving changes who's in its channel (R9-A5): the lists follow.
    onSuccess: () => invalidate("teams", "team", "resources", "people", "conversations"),
  });
}

/** What deleting an archived team takes with it (D29, R10-Q2): asked when the dialog opens. */
export function useTeamDeleteSummary(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ["team", id, "delete-summary"] as const,
    queryFn: () =>
      unwrap(api.GET("/api/teams/{team_id}/delete-summary", { params: { path: { team_id: id } } })),
    enabled,
    staleTime: 0,
  });
}

/** Delete an archived team for good (D29, R10-Q2), its name typed to confirm. */
export function useDeleteTeam() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, confirmName }: { id: string; confirmName: string }) =>
      unwrap(
        api.DELETE("/api/teams/{team_id}", {
          params: { path: { team_id: id } },
          body: { confirm_name: confirmName },
        }),
      ),
    onSuccess: () => invalidate("teams", "resources", "people", "conversations"),
  });
}

export function useUpdateSettings() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (values: Record<string, Record<string, unknown>>) =>
      unwrap(api.PATCH("/api/settings", { body: { values } })),
    // `me.email_enabled` follows the email switch (R6): Email me shows at once.
    onSuccess: () => invalidate("settings", "me"),
  });
}

export function useSignOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // A delete still waiting behind its Undo goes now, while the session lasts (R9-A8).
      await flushWaiting();
      // This browser stops getting the person's pushes (PLAN §8.7): whoever signs in next
      // must not see them.
      await dropDevice((endpoint) =>
        unwrap(api.DELETE("/api/push/subscribe", { body: { endpoint } })),
      );
      return unwrap(api.POST("/api/auth/logout"));
    },
    onSettled: () => {
      qc.clear();
      clearDrafts(); // R9-A10: drafts are this person's (whoever signs in next starts clean)
      forgetPlace(); // P2-A8: so is the last place
    },
  });
}

export const TOOLS: { tool: Tool; title: string; icon: string; sub?: string }[] = [
  { tool: "redmine", title: "Redmine projects", icon: "i-ticket" },
  { tool: "github", title: "GitHub repositories", icon: "i-pr", sub: "in bcsw" },
  { tool: "outline", title: "Outline collections", icon: "i-doc" },
];
